import { styled } from "@linaria/react";
import createIOSortType from "../../../lib/createIOSortType";
import createGlobalState from "@ui/lib/createGlobalState";
import { MIN_ROW_HEIGHT } from "@ui/Table/Tr";
import * as io from "io-ts";
import type { SortableValue } from "diagnostics-lib/comparator";
import { useModals } from "@ui/lib/hooks/useModal";
import React from "react";
import useSortedData from "../../../lib/hooks/useSortedData";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import Modal from "@ui/Modal";
import t from "../../../i18n";
import Table from "@ui/Table";
import type { Users, UserProfil } from "../../../api/useUsers";
import GlobalRoleMenu from "./GlobalRoleMenu";
import { useUser } from "../../../api/useUser";
import ProjectRoles from "./ProjectRoles";
import ExpandArrow from "./ExpandArrow";
import DeleteUserAction from "./DeleteUserAction";
import DeleteUserModalForm from "./DeleteUserModalForm";
import AddProjectModalForm from "./AddProjectModalForm";
import {
  canUserDo,
  canUserDoOnProject,
  canUserDoOnSomeProject,
} from "diagnostics-domain/canUserDo";
import Button from "@ui/Button";
import { AddCircleLine } from "@ui/IconV2/react";
import { getGlobalRoleName } from "./globalRoleOptions";

export type ModalType = "addProject" | "deleteUser";

interface P {
  users: Users;
  loading: boolean;
}

const Name = styled.div`
  display: flex;
  height: ${MIN_ROW_HEIGHT}px;
  align-items: center;
`;

const ExpandedRow = styled.div`
  width: 100%;
  margin-bottom: 10px;
`;

const ProjectCounter = styled.div`
  width: 100%;
`;
const IdWrapper = styled.div`
  display: flex;
  width: 100%;
  height: ${MIN_ROW_HEIGHT}px;
  align-items: center;
`;

const Id = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RoleWrapper = styled.div`
  display: flex;
  align-items: center;
  height: ${MIN_ROW_HEIGHT}px;
`;

const ProjectRoleWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  width: 100%;
`;

const SuperAdminRole = styled.p`
  cursor: default;
`;

const columns = ["login", "name", "globalRoles", "projectRoles"] as const;
type Column = (typeof columns)[number];

const userRowsExpandedType = io.record(io.string, io.boolean);
const useUserRowsExpanded = createGlobalState({
  storage: "session",
  key: "userRowsExpanded",
  type: userRowsExpandedType,
  defaultValue: {},
  scoped: false,
});

const useSort = createGlobalState({
  storage: "session",
  key: "usersSort",
  type: createIOSortType(columns),
  defaultValue: {
    column: "login",
    direction: "asc",
  },
  scoped: false,
});

const computeRowKey = (data: UserProfil) => `user-id-${data.login}`;

const SORT_SELECTORS: { [key in Column]: (u: UserProfil) => SortableValue } = {
  login: u => u.login,
  name: u => u.name ?? "",
  globalRoles: u => getGlobalRoleName(u.roles.global) ?? "",
  projectRoles: u =>
    u.roles.global === "ADMIN" || u.roles.global === "SUPER_ADMIN" ? 0 : u.roles.projects.length,
};

const UsersTable = ({ loading, users }: P) => {
  const loggedInUser = useUser();
  const [userRowsExpanded, setuserRowsExpanded] = useUserRowsExpanded({});
  const { modal, openModal, closeModal } = useModals<ModalType>();
  const [userProfil, setUserProfil] = React.useState<UserProfil>();
  const [sort, setSort] = useSort({});

  const datas = useSortedData(users, SORT_SELECTORS[sort.column], sort.direction);

  const handleDelete = React.useCallback(
    (userProfil: UserProfil) => (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setUserProfil(userProfil);
      openModal("deleteUser");
    },
    [openModal, setUserProfil],
  );

  const handleAddProject = React.useCallback(
    (userProfil: UserProfil) => (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setUserProfil(userProfil);
      openModal("addProject");
    },
    [openModal],
  );

  const handleToggleRoles = React.useCallback(
    (userProfil: UserProfil, e: React.MouseEvent) => {
      if (e.target instanceof HTMLAnchorElement) return;
      if (userProfil.roles.projects.length > 0) {
        const newState = { ...userRowsExpanded };
        newState[userProfil.login] = !userRowsExpanded[userProfil.login];
        setuserRowsExpanded(newState);
      }
    },
    [setuserRowsExpanded, userRowsExpanded],
  );

  const isRowInteractive = React.useCallback(
    (userProfil: UserProfil) =>
      userProfil.roles.global !== "ADMIN" &&
      Boolean(
        userProfil.roles.projects.filter(project =>
          canUserDoOnProject(loggedInUser, "ASSIGN_PROJECT_ROLE", project.projectId),
        ).length,
      ),
    [loggedInUser],
  );

  const metadata = React.useMemo(() => {
    const _: { [key in Column]: ColumnMetaData<UserProfil> } = {
      login: {
        label: t("users.paragraphs.users.table.column.id"),
        alignment: "left",
        verticalAlignmentCenter: false,
        sticky: true,
        stickyBorder: false,
        sortable: true,
        minWidth: "200px",
        maxWidth: "290px",
        render: u => (
          <IdWrapper>
            <ExpandArrow userProfil={u} userRowsExpanded={userRowsExpanded} />
            <Id title={u.login}>{u.login}</Id>
            {u.roles.global !== "SUPER_ADMIN" && (
              <DeleteUserAction onClick={handleDelete(u)} user={loggedInUser} />
            )}
          </IdWrapper>
        ),
      },
      name: {
        label: t("users.paragraphs.users.table.column.name"),
        alignment: "left",
        verticalAlignmentCenter: false,
        stickyBorder: false,
        sortable: true,
        minWidth: "200px",
        maxWidth: "250px",
        render: u => (u.name ? <Name>{u.name}</Name> : null),
      },
      globalRoles: {
        label: t("users.paragraphs.users.table.column.global_roles"),
        alignment: "left",
        verticalAlignmentCenter: false,
        sortable: true,
        minWidth: "250px",
        maxWidth: "250px",
        render: u => (
          <RoleWrapper>
            {u.roles.global === "SUPER_ADMIN" ? (
              <SuperAdminRole>{getGlobalRoleName(u.roles.global)}</SuperAdminRole>
            ) : (
              <GlobalRoleMenu userProfil={u} user={loggedInUser} />
            )}
          </RoleWrapper>
        ),
      },
      projectRoles: {
        label: t("users.paragraphs.users.table.column.project_roles"),
        alignment: "left",
        sortable: true,
        minWidth: "300px",
        render: u => {
          if (u.roles.global === "ADMIN" || u.roles.global === "SUPER_ADMIN") return null;

          const allowAssignRole = canUserDo(loggedInUser, "ASSIGN_PROJECT_ROLE");
          const allowProjectAssignRole = canUserDoOnSomeProject(
            loggedInUser,
            "ASSIGN_PROJECT_ROLE",
          );
          const allowedProjects = loggedInUser.roles.projects.map(({ projectId }) => projectId);

          const editableProjects = u.roles.projects.filter(project =>
            canUserDoOnProject(loggedInUser, "ASSIGN_PROJECT_ROLE", project.projectId),
          );
          const hasAllowedEditableProjects =
            loggedInUser.roles.global === "ADMIN" ||
            loggedInUser.roles.global === "SUPER_ADMIN" ||
            allowedProjects.some(id => !editableProjects.find(({ projectId }) => projectId === id));

          const expanded = userRowsExpanded[u.login] && editableProjects.length > 0;

          if (editableProjects.length === 0) {
            return (
              (allowAssignRole || allowProjectAssignRole) &&
              hasAllowedEditableProjects && (
                <Button onClick={handleAddProject(u)} variant="link" leftIcon={<AddCircleLine />}>
                  {t("users.table.row.add_project")}
                </Button>
              )
            );
          }

          return (
            <ProjectRoleWrapper>
              {expanded && (
                <ExpandedRow>
                  {editableProjects.length > 0 ? (
                    <ProjectRoles
                      userProjects={editableProjects}
                      userProfil={u}
                      user={loggedInUser}
                    />
                  ) : null}
                  {(allowAssignRole || allowProjectAssignRole) && hasAllowedEditableProjects && (
                    <Button
                      onClick={handleAddProject(u)}
                      variant="link"
                      leftIcon={<AddCircleLine />}
                    >
                      {t("users.table.row.add_project")}
                    </Button>
                  )}
                </ExpandedRow>
              )}
              <div>
                {!expanded && editableProjects.length > 0 && (
                  <ProjectCounter>
                    {t("users.table.row.projects_count", {
                      count: editableProjects.length,
                    })}
                  </ProjectCounter>
                )}
              </div>
            </ProjectRoleWrapper>
          );
        },
      },
    };
    return _;
  }, [userRowsExpanded, handleDelete, loggedInUser, handleAddProject]);

  return (
    <>
      {userProfil !== undefined && (
        <Modal
          title={t("users.modal.add_project_to_user.add_project")}
          isOpen={modal === "addProject"}
          onRequestClose={closeModal}
        >
          <AddProjectModalForm userProfil={userProfil} onRequestClose={closeModal} />
        </Modal>
      )}
      {userProfil !== undefined && (
        <Modal
          isOpen={modal === "deleteUser"}
          onRequestClose={closeModal}
          title={t("users.modal.delete.delete_user")}
        >
          <DeleteUserModalForm userProfil={userProfil} onRequestClose={closeModal} />
        </Modal>
      )}
      <Table
        stickyHeader
        loading={loading}
        datas={datas}
        metadata={metadata}
        computeRowKey={computeRowKey}
        sort={sort}
        onSort={setSort}
        onRowClick={handleToggleRoles}
        isRowInteractive={isRowInteractive}
      />
    </>
  );
};

export default React.memo(UsersTable);
